// @flow
import React from 'react';
import { createMuiTheme, MuiThemeProvider } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';

type Props = {
  children: Object,
};

type State = {
  theme: string,
  bold: string,
};

export const SiteThemeContext = React.createContext<Object>({
  theme: 'light',
});

export class SiteThemeProvider extends React.Component<Props, State> {
  componentDidMount() {
    if (window.isElectron) {
      window.electronIPC.onToggleTheme(this.handleThemeChange);
    }
  }

  state = {
    theme: window.localStorage.getItem('scribe_theme') || 'light',
    bold: window.localStorage.getItem('scribe_theme_bold') || 'false',
  };

  handleThemeChange = () => {
    const { theme } = this.state;
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    window.localStorage.setItem('scribe_theme', newTheme);
    if (window.isElectron) {
      window.electronIPC.sendTheme(newTheme);
    }
    this.setState({ theme: newTheme });
  };

  handleBoldThemeChange = () => {
    const { bold } = this.state;
    window.localStorage.setItem('scribe_theme_bold', bold === 'true' ? 'false' : 'true');
    this.setState({ bold: bold === 'true' ? 'false' : 'true' });
  };

  render() {
    const { theme, bold } = this.state;
    const colors = {
      primary: { main: '#000000', dark: '#0F1316', light: '#FFFFFF' },
      secondary: { main: '#C5C8C6', dark: '#3F4044', light: '#FFFFFF' },
      contrast: {
        dark: '#FFFFFF',
        light: '#0F1316',
      },
      red: {
        dark: '#b71c1c',
        light: '#ffcdd2',
      },
      background: {
        dark: '#0F1316',
        default: theme === 'dark' ? '#0F1316' : '#FFFFFF',
        light: '#FFFFFF',
      },
      border: {
        dark: '#555555',
        light: '#DDDDDD',
      },
      text: {
        dark: {
          primary: '#EEEEEE',
          secondary: '#DDDDDD',
        },
        light: {
          primary: '#4B4B4B',
          secondary: '#9B9B9B',
        },
      },
    };

    const ava = {
      accent: '#FC9C21',
      blue: '#50ACEE',
      blue1: '#0679B4',
      blue2: '#50ACED',
      blue4: '#B0D9F8',
      blue5: '#D0E9FB',
      dark1: '#000000',
      dark2: '#101010',
      dark4: '#232324',
      dark5: '#2D2F34',
      darkBlue: '#214a88',
      darkBg1: 'linear-gradient(131.91deg, #112967 19.06%, #272728 81.95%)',
      darkBg2: 'linear-gradient(229.3deg, #000000 5.57%, #000E34 80.9%)',
      deepBlue: '#1B367C',
      gradientBlue: 'linear-gradient(135deg, #214a88 25%, #438fd0 85%)',
      grey: '#9B9B9B',
      grey4: '#474B43',
      grey5: '#778290',
      grey6: '#DADCE0',
      green: '#34C54F',
      green2: '#009778',
      navy: '#102867',
      navy2: '#035A98',
      navy5: '#F4F9FF',
      offBlue1: '#F6F8FF',
      red2: '#DE423E',
      red3: '#E66E6B',
      red4: '#ED9996',
      red5: '#F5C3C2',
      scribe1: '#238736',
      scribe2: '#02C874',
      selfColorBlue: '#035A98',
      selfColorBlueDark: '#155D7C',
      white: '#FFFFFF',
      whiteGradient: 'linear-gradient(131.94deg, #F7FBFF 15.67%, #D5D7D9 85.55%)',
      icon: {
        dark1: '#B0D9F8',
        darkBg: '#5B575E',
      },
      text: {
        dark1: '#F2F2F2',
        dark2: '#DADCE0',
        grey: '#7B7C80',
      },
      text1: '#778290',
      states: {
        online: '#34C54F',
      },
      navyGroup: {
        convoText: '#035A98',
        convoTextDark: '#81C3F3',
      },
    };

    const t = {
      palette: {
        type: theme,
        primary: colors.primary,
        secondary: colors.secondary,
        background: colors.background,
        contrast: colors.contrast,
        border: colors.border[theme],
        divider: colors.border[theme],
        text: colors.text[theme],
        red: colors.red[theme],
        bold,
        ava,
        ccMode: {
          conversationButtonsBg: 'rgba(38, 50, 82, 0.9)',
          backgroundGradient: 'linear-gradient(180deg, rgba(38, 50, 82, 0.9) 0%, rgba(39, 39, 40, 0.9) 100%)',
        },
      },
      typography: {
        useNextVariants: true,
        fontFamily: [
          'AdelleSans',
          'Source Sans Pro',
          '-apple-system',
          'BlinkMacSystemFont',
          '"Segoe UI"',
          'Roboto',
          '"Helvetica Neue"',
          'Arial',
          'sans-serif',
          '"Apple Color Emoji"',
          '"Segoe UI Emoji"',
          '"Segoe UI Symbol"',
        ].join(','),
      },
      breakpoints: {
        values: {
          xs: 0,
          sm: 600,
          md: 900,
          lg: 1200,
          xl: 1536,
        },
      },
      overrides: {
        MuiSwitch: {
          colorPrimary: {
            '&$checked': {
              color: '#50acef',
              '& + $track': {
                backgroundColor: ava.blue,
              },
            },
          },
        },
        MuiOutlinedInput: {
          root: {
            '& $notchedOutline legend': {
              borderBottom: 'none',
            },
            '&$focused $notchedOutline': {
              borderColor: ava.blue,
              fontWeight: 400,
            },
          },
        },
        MuiFormLabel: {
          root: {
            '&$focused': {
              color: '#9B9B9B',
            },
            borderBottom: 'none',
            fontWeight: 400,
          },
        },
        MuiInputLabel: {
          root: {
            '&$focused': {
              color: ava.blue,
            },
            '&$error': {
              color: '#f44336',
            },
          },
        },
        MuiButton: {
          root: {
            textTransform: 'none',
          },
          containedPrimary: {
            '&:hover': {
              backgroundColor: ava.blue,
            },
            backgroundColor: ava.blue,
          },
          contained: {
            '&$disabled': {
              color: 'white',
              opacity: 0.3,
              backgroundColor: ava.blue,
            },
          },
        },
        MuiIconButton: {
          root: {
            padding: '4px 12px',
          },
        },
        MuiListItemIcon: {
          root: {
            minWidth: 44,
          },
        },
        MuiRadio: {
          root: {
            color: ava.blue,
          },
          colorSecondary: {
            '&$checked': {
              color: ava.blue,
            },
          },
        },
      },
    };
    const themeMaterial = createMuiTheme(t);
    const { children } = this.props;
    return (
      <SiteThemeContext.Provider
        value={{
          ...this.state,
          handleThemeChange: this.handleThemeChange,
          handleBoldThemeChange: this.handleBoldThemeChange,
        }}
      >
        <MuiThemeProvider theme={themeMaterial}>
          <CssBaseline />
          {children}
        </MuiThemeProvider>
      </SiteThemeContext.Provider>
    );
  }
}
